import React, { useEffect, useState } from "react";
import { Box, Button, Grid2, Stack, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TableComponent from "components/grid";
import PaginationComponent from "components/ui-components/PaginationComponent";
import { useParams } from "react-router-dom";
import moment from "moment";
import EditPM from "./EditPM";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { IoMdSearch } from "react-icons/io";
import { useGetPendingPMQuery } from "store/slices/apis/keyPMSlice";
import ApprovePMComponent from "components/action/ApprovePMComponent";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";
import RejectPMComponent from "components/action/RejectPMComponent";

const columns = [
  {
    name: "PM ID",
    field: "tag",
    renderer(params) {
      return (
        <Stack direction="row" spacing={1}>
          {params?.pm_number && (
            <Chip
              label={params?.pm_number}
              size="small"
              sx={{
                fontSize: "12px",
                "& .MuiChip-label": {
                  paddingLeft: "8px",
                  paddingRight: "8px",
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Stack>
      );
    },
  },
  {
    name: "PM Name",
    field: "pm",
  },
  {
    name: "Category",
    field: "category",
  },
  {
    name: "Owned",
    field: "owned",
    renderer(params) {
      return <>{params?.owned ? "True" : "False"}</>;
    },
  },
  {
    name: "Plan Deployment Date",
    field: "plannedDate",
    renderer(params) {
      return (
        <>
          {params?.plannedDate
            ? moment(params.plannedDate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Actual Deployment Date",
    field: "actualDate",
    renderer(params) {
      return (
        <>
          {params?.actualDate
            ? moment(params.actualDate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Submission Date",
    field: "createdDate",
    renderer(params) {
      return (
        <>
          {params?.createdDate
            ? moment(params.createdDate).format("DD/MM/YY")
            : "N.A."}
        </>
      );
    },
  },
  {
    name: "Workflow Status",
    field: "workFlowStatus",
  },
  {
    name: "Action",
    field: "action",
    option: {
      view: false,
      delete: false,
      edit: false,
      acceptReject: true,
    },
  },
];

const PMPendingApproval = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [pageNumber, setPageNumber] = useState(1);
  const [isIssueReject, setIsIssueReject] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [fullObjectData, setFullObjectData] = useState(null);
  const [isIssueApprove, setIsIssueApprove] = useState(false);
  const { projectId } = useParams();
  const [approvedStatus, setApprovedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filters, setFilters] = useState({
    approvedStatus: "",
    fromDate: "",
    toDate: "",
  });
  const {
    data: allPM,
    refetch,
    isLoading,
  } = useGetPendingPMQuery({
    ProjectId: projectId,
    sortBy: "CreatedDate",
    sortOrder: "desc",
    status: "Pending",
    from: filters?.fromDate,
    to: filters?.toDate,
    page: pageNumber,
    pageSize: 10,
  });
  const [issueData, setEditIssueData] = useState(null);
  const [isViewIssueModal, setIsViewIssueModal] = useState(false);
  let allPMList = allPM?.data?.map((item) => {
    const parsedData = JSON.parse(item.data);
    const updatedParsedData = {
      ...parsedData,
      workFlowStatus: item?.status,
    };

    return {
      ...item,
      ...updatedParsedData,
    };
  });

  useEffect(() => {
    refetch();
  }, []);
  const openNotification = async (msg, type = "error") => {
    await dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: {
          color: type,
        },
        close: false,
      })
    );

    setTimeout(() => dispatch(closeSnackbar()), 3000);
  };

  const handleActionAcceptIssue = (row) => {
    const fullObject = allPM?.data?.find((item) => item.id !== row.id);
    if (fullObject) {
      setIsIssueApprove((prev) => {
        const newState = !prev;
        setRowData(newState ? row : null);
        setFullObjectData(newState ? fullObject : null);
        return newState;
      });
    }
  };

  const handleCancelIssue = (row) => {
    setIsIssueApprove((prev) => {
      const newState = !prev;
      setRowData(newState ? row : null);
      return newState;
    });
  };

  const handleCancelReject = (row) => {
    setIsIssueReject((prev) => {
      const newState = !prev;
      setRowData(newState ? row : null);
      return newState;
    });
  };

  const handleActionView = (row) => {
    setIsViewIssueModal(!isViewIssueModal);
    if (!issueData) {
      setEditIssueData(row);
    } else {
      setEditIssueData(null);
    }
  };

  const handleActionRejectIssue = (row) => {
    const fullObject = allPM?.data?.find((item) => item.id !== row.id);
    if (fullObject) {
      setIsIssueReject((prev) => {
        const newState = !prev;
        setRowData(newState ? row : null);
        setFullObjectData(newState ? fullObject : null);
        return newState;
      });
    }
  };

  return (
    <>
      <Grid2 container spacing={2} sx={{ mt: "20px" }}>
        <Grid2 size={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                flexGrow: 2,
                alignItems: "center",
                "& .MuiStack-root": {
                  paddingTop: "15px",
                },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} size="small">
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    format="DD-MM-YYYY"
                    onChange={(newValue) => setFromDate(newValue)}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: "",
                            toDate: toDate
                              ? dayjs(toDate).format("YYYY-MM-DD")
                              : "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker", "DatePicker"]}>
                  <DatePicker
                    label="To Date"
                    value={toDate}
                    format="DD-MM-YYYY"
                    clearable={true}
                    onChange={(newValue) => setToDate(newValue)}
                    slotProps={{
                      textField: {
                        sx: {
                          minWidth: 150,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px",
                          },

                          "& .MuiInputLabel-root": {
                            top: "-6px",
                          },
                        },
                      },
                      field: {
                        clearable: true,
                        onClear: () =>
                          setFilters({
                            fromDate: fromDate
                              ? dayjs(fromDate).format("YYYY-MM-DD")
                              : "",
                            toDate: "",
                            approvedStatus,
                          }),
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFilters({
                    fromDate: fromDate
                      ? dayjs(fromDate).format("YYYY-MM-DD")
                      : "",
                    toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : "",
                    approvedStatus,
                  });
                }}
                size="large"
                startIcon={<IoMdSearch />}
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 20px",
                  marginTop: "12px !important",
                }}
              >
                Search
              </Button>
              <Button
                loading={isLoading}
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  const clearedValues = Object.fromEntries(
                    Object.keys(filters).map((key) => [key, ""])
                  );
                  setFilters(clearedValues);
                }}
                size="large"
                variant="outlined"
                sx={{
                  color: `${theme?.customSettings?.themeColor}`,
                  border: `1px solid ${theme?.customSettings?.themeColor}`,
                  background: "none",
                  textTransform: "inherit",
                  padding: "6px 15px",
                  marginTop: "12px !important",
                }}
              >
                Reset
              </Button>
            </Stack>
          </Box>
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <TableComponent
            columns={columns}
            data={allPMList}
            actionView={handleActionView}
            actionAccept={handleActionAcceptIssue}
            actionReject={handleActionRejectIssue}
            isLoading={isLoading}
          />
          {!isLoading && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <PaginationComponent
                pageNumber={allPM?.pagination?.currentPage || 1}
                totalPages={allPM?.pagination?.totalPages || 1}
                onPageChange={setPageNumber}
              />
            </Box>
          )}
        </Grid2>
      </Grid2>
      {isViewIssueModal && (
        <EditPM
          cancelIssue={handleActionView}
          open={isViewIssueModal}
          data={issueData}
          isViewIssueModal
        />
      )}
      <ApprovePMComponent
        open={isIssueApprove}
        cancel={handleCancelIssue}
        rowData={rowData}
        fullObjectData={fullObjectData}
        openNotification={openNotification}
        refetch={refetch}
      />
      <RejectPMComponent
        open={isIssueReject}
        cancel={handleCancelReject}
        rowData={rowData}
        fullObjectData={fullObjectData}
        openNotification={openNotification}
        refetch={refetch}
      />
    </>
  );
};

export default PMPendingApproval;
